import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import logo from '../../../assets/new-logo.png';
import api from '../../../services/api';
import { useWebsiteBuilder } from '../context/WebsiteBuilderContext';

const FONT_OPTIONS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Raleway', 'Playfair Display', 'Merriweather', 'Nunito',
];

const CTA_PRESETS = [
  'Book Now', 'Get a Quote', 'Shop Now', 'Schedule Consultation',
  'Contact Us', 'Get Started', 'Learn More', 'Request a Demo',
];

// ── Reusable Badges & Form Helpers ──────────────────────────────────────────

function LockedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 shadow-2xs">
      <i className="ri-lock-line text-[10px]" />
      Pre-filled
    </span>
  );
}

function RecommendedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-0.5 shadow-2xs">
      <i className="ri-star-fill text-[10px] text-amber-500" />
      Recommended
    </span>
  );
}

interface FieldProps {
  label: string;
  locked?: boolean;
  recommended?: boolean;
  children: React.ReactNode;
  helper?: string;
}

function FormField({ label, locked, recommended, children, helper }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {locked && <LockedBadge />}
        {recommended && <RecommendedBadge />}
      </div>
      {children}
      {helper && <p className="text-xs text-slate-500 leading-relaxed">{helper}</p>}
    </div>
  );
}

interface InputProps {
  value: string;
  onChange: (v: string) => void;
  locked: boolean;
  placeholder?: string;
  type?: string;
  rows?: number;
}

function TextInput({ value, onChange, locked, placeholder, type = 'text' }: InputProps) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => !locked && onChange(e.target.value)}
      readOnly={locked}
      placeholder={locked ? '' : placeholder}
      className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors ${
        locked
          ? 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed select-none'
          : 'bg-white border-slate-300 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
      }`}
    />
  );
}

function TextAreaInput({ value, onChange, locked, placeholder, rows = 3 }: InputProps) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => !locked && onChange(e.target.value)}
      readOnly={locked}
      placeholder={locked ? '' : placeholder}
      rows={rows}
      className={`w-full px-3 py-2.5 rounded-lg border text-sm outline-none resize-none transition-colors ${
        locked
          ? 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed select-none'
          : 'bg-white border-slate-300 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
      }`}
    />
  );
}

// ── Main Page Component ────────────────────────────────────────────────────

export default function WebsiteBuilderOnboardingPage() {
  const navigate = useNavigate();
  const { setIsOnboardingCompleted } = useWebsiteBuilder();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockedFields, setLockedFields] = useState<Record<string, boolean>>({});

  // Form Fields
  const [brandName, setBrandName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [primaryBusiness, setPrimaryBusiness] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [businessTagline, setBusinessTagline] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryFont, setPrimaryFont] = useState('Inter');
  const [differentiators, setDifferentiators] = useState<string[]>(['', '', '']);
  const [services, setServices] = useState<Array<{ name: string; description: string; priority: boolean }>>([]);
  const [products, setProducts] = useState<Array<{ name: string; description: string; priority: boolean }>>([]);
  const [businessAddress, setBusinessAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [customerDescription, setCustomerDescription] = useState('');
  const [preferredCta, setPreferredCta] = useState('');
  const [customCta, setCustomCta] = useState('');

  // Load existing profile & locked status on mount
  useEffect(() => {
    loadOnboardingData();
  }, []);

  const loadOnboardingData = async () => {
    try {
      setIsLoading(true);
      const res = await api.getWbOnboardingData();
      const data = res?.data?.data || res?.data;

      const f = data?.fields || {};
      const locked = data?.locked_fields || {};
      setLockedFields(locked);

      setBrandName(f.brand_name || f.business_name || '');
      setBusinessType(f.business_type || '');
      setPrimaryBusiness(f.primary_business || '');
      setBusinessDescription(f.business_description || '');
      setBusinessTagline(f.business_tagline || '');
      setLogoUrl(f.logo_url || '');
      setPrimaryFont(f.primary_font || 'Inter');
      setDifferentiators(
        f.differentiators && f.differentiators.length > 0
          ? f.differentiators
          : ['', '', '']
      );
      setServices(
        (f.services || []).map((s: any) => ({
          name: s.name || '',
          description: s.description || '',
          priority: s.priority || false,
        }))
      );
      setProducts(
        (f.products || []).map((p: any) => ({
          name: p.name || '',
          description: p.description || '',
          priority: p.priority || false,
        }))
      );
      setBusinessAddress(f.business_address || '');
      setCity(f.city || '');
      setState(f.state || '');
      setCountry(f.country || '');
      setPostalCode(f.postal_code || '');
      setBusinessPhone(f.business_phone || '');
      setBusinessEmail(f.business_email || localStorage.getItem('email') || '');
      setFacebookUrl(f.facebook_url || '');
      setInstagramUrl(f.instagram_url || '');
      setLinkedinUrl(f.linkedin_url || '');
      setTwitterUrl(f.twitter_url || '');
      setYoutubeUrl(f.youtube_url || '');
      setCustomerDescription(f.customer_description || '');

      const cta = f.preferred_cta || '';
      if (CTA_PRESETS.includes(cta)) {
        setPreferredCta(cta);
      } else if (cta) {
        setPreferredCta('__custom__');
        setCustomCta(cta);
      }
    } catch (err) {
      console.error('Failed to load website builder onboarding data:', err);
      toast.error('Failed to load profile. Please refresh to try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors: string[] = [];
    if (!brandName.trim()) errors.push('Brand Name is required');
    if (!businessType.trim()) errors.push('Business Type is required');
    if (!businessDescription.trim()) errors.push('Business Description is required');

    const filledDiffs = differentiators.filter((d) => d && d.trim());
    if (filledDiffs.length === 0) {
      errors.push('At least one Brand Differentiator is required');
    }

    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    const finalCta = preferredCta === '__custom__' ? customCta.trim() : preferredCta;

    try {
      setIsSubmitting(true);
      const payload = {
        brand_name: brandName,
        business_name: brandName,
        business_type: businessType,
        primary_business: primaryBusiness,
        business_description: businessDescription,
        business_tagline: businessTagline.trim() || null,
        primary_font: primaryFont,
        differentiators: filledDiffs,
        services: services.filter((s) => s.name.trim()),
        products: products.filter((p) => p.name.trim()),
        business_address: businessAddress,
        city,
        state,
        country,
        postal_code: postalCode,
        business_phone: businessPhone,
        facebook_url: facebookUrl,
        instagram_url: instagramUrl,
        linkedin_url: linkedinUrl,
        twitter_url: twitterUrl,
        youtube_url: youtubeUrl,
        customer_description: customerDescription,
        preferred_cta: finalCta || null,
      };

      await api.completeWbOnboarding(payload);
      setIsOnboardingCompleted(true);
      toast.success('Website profile configured! Launching Website Builder...');
      setTimeout(() => {
        navigate('/website-builder/sitemap', { replace: true });
      }, 700);
    } catch (err: any) {
      console.error('Failed to complete onboarding:', err);
      toast.error(err?.message || 'Failed to save setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateDiff = (idx: number, val: string) => {
    setDifferentiators((prev) => prev.map((d, i) => (i === idx ? val : d)));
  };
  const addDiff = () => setDifferentiators((prev) => [...prev, '']);
  const removeDiff = (idx: number) => setDifferentiators((prev) => prev.filter((_, i) => i !== idx));

  const updateService = (idx: number, field: string, val: any) =>
    setServices((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));
  const addService = () => setServices((prev) => [...prev, { name: '', description: '', priority: false }]);
  const removeService = (idx: number) => setServices((prev) => prev.filter((_, i) => i !== idx));

  const updateProduct = (idx: number, field: string, val: any) =>
    setProducts((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: val } : p)));
  const addProduct = () => setProducts((prev) => [...prev, { name: '', description: '', priority: false }]);
  const removeProduct = (idx: number) => setProducts((prev) => prev.filter((_, i) => i !== idx));

  const lk = lockedFields;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Loading your brand setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-4xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Marketing 4SIGHT" className="h-8" />
            <div className="h-4 w-px bg-slate-300 hidden sm:block" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:block">
              Website Builder Setup
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
              <i className="ri-shield-check-line text-teal-600" />
              Pre-filled data synced from Brand Profile
            </span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-6 pt-8 space-y-6">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-teal-900 via-teal-800 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-md relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-xs border border-white/20 text-teal-200 rounded-full px-3 py-1 text-xs font-medium mb-3">
              <i className="ri-magic-line" />
              Automated Pre-fill Engine
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
              Website Builder Onboarding
            </h1>
            <p className="text-teal-100/90 text-sm leading-relaxed">
              We've pre-filled your business details from your onboarding profile. Review the information, fill in any missing details, and set your unique Tagline and Call-to-Action to generate your custom website.
            </p>
          </div>
          <div className="absolute -right-8 -bottom-10 w-48 h-48 bg-teal-500/10 rounded-full blur-2xl pointer-events-none" />
        </div>

        {/* ── Card 1: Business Fundamentals ───────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5 border-b border-slate-100 pb-4">
            <div className="w-8 h-8 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center">
              <i className="ri-building-line text-teal-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Business Fundamentals</h2>
              <p className="text-xs text-slate-500">Core company details that define your website architecture</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Brand Name" locked={lk.brand_name}>
              <TextInput value={brandName} onChange={setBrandName} locked={!!lk.brand_name} placeholder="e.g. UrsDigitally" />
            </FormField>

            <FormField label="Business Type" locked={lk.business_type}>
              {lk.business_type ? (
                <TextInput value={businessType} onChange={setBusinessType} locked={true} />
              ) : (
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                >
                  <option value="">Select offering model...</option>
                  <option value="Service">Service</option>
                  <option value="Product">Product</option>
                  <option value="Both">Both (Service & Product)</option>
                </select>
              )}
            </FormField>

            <div className="md:col-span-2">
              <FormField label="Industry / Business Category" locked={lk.primary_business}>
                <TextInput
                  value={primaryBusiness}
                  onChange={setPrimaryBusiness}
                  locked={!!lk.primary_business}
                  placeholder="e.g. Digital Marketing & Business Consulting"
                />
              </FormField>
            </div>

            <div className="md:col-span-2">
              <FormField label="Business Description" locked={lk.business_description}>
                <TextAreaInput
                  value={businessDescription}
                  onChange={setBusinessDescription}
                  locked={!!lk.business_description}
                  placeholder="Briefly describe what your business does..."
                  rows={3}
                />
              </FormField>
            </div>

            {/* Recommended Field: Business Tagline */}
            <div className="md:col-span-2 bg-amber-50/50 border border-amber-200/70 rounded-xl p-4">
              <FormField
                label="Business Tagline / Slogan"
                recommended
                helper="This catchy slogan appears next to your brand name in the top announcement strip and hero banner."
              >
                <TextInput
                  value={businessTagline}
                  onChange={setBusinessTagline}
                  locked={false}
                  placeholder='e.g. "Transforming Brands Through Data-Driven Marketing"'
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* ── Card 2: Visual Identity & USPs ─────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5 border-b border-slate-100 pb-4">
            <div className="w-8 h-8 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center">
              <i className="ri-palette-line text-purple-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Brand Identity & Differentiators</h2>
              <p className="text-xs text-slate-500">Visual elements and Unique Selling Points for your landing pages</p>
            </div>
          </div>

          <div className="space-y-4">
            <FormField label="Brand Logo" locked={lk.logo_url}>
              {logoUrl ? (
                <div className="flex items-center gap-4 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="h-14 w-28 bg-white border border-slate-200 rounded-lg p-2 flex items-center justify-center overflow-hidden">
                    <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-700">Logo loaded from Brand Profile</p>
                    <p className="text-xs text-slate-400 mt-0.5">Used for color extraction, header navbar, and footer.</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 border border-dashed border-slate-300 rounded-xl text-center text-slate-500 text-sm">
                  <i className="ri-image-line text-xl mb-1 block text-slate-400" />
                  No logo uploaded yet. You can upload one in Profile Settings.
                </div>
              )}
            </FormField>

            <FormField label="Primary Brand Font" locked={lk.primary_font} helper="Defines the heading and body typography across your website pages.">
              {lk.primary_font ? (
                <TextInput value={primaryFont} onChange={setPrimaryFont} locked={true} />
              ) : (
                <select
                  value={primaryFont}
                  onChange={(e) => setPrimaryFont(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              )}
            </FormField>

            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <label className="text-sm font-medium text-slate-700">Brand Differentiators / USPs</label>
                {lk.differentiators && <LockedBadge />}
              </div>
              <p className="text-xs text-slate-500 mb-3">Powers the USP ribbon and competitive advantage callouts.</p>

              <div className="space-y-2">
                {differentiators.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-5 shrink-0 text-center">{i + 1}</span>
                    <input
                      value={d}
                      onChange={(e) => !lk.differentiators && updateDiff(i, e.target.value)}
                      readOnly={!!lk.differentiators}
                      placeholder={`USP ${i + 1}, e.g. "100% Data-Driven Campaign Execution"`}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
                        lk.differentiators
                          ? 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                    {!lk.differentiators && differentiators.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDiff(i)}
                        className="text-slate-400 hover:text-red-500 p-1.5 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!lk.differentiators && differentiators.length < 5 && (
                <button
                  type="button"
                  onClick={addDiff}
                  className="mt-3 text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer"
                >
                  <i className="ri-add-line text-sm" /> Add Differentiator
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Card 3: Offerings (Products / Services) ─────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5 border-b border-slate-100 pb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
              <i className="ri-store-2-line text-blue-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Offerings Architecture</h2>
              <p className="text-xs text-slate-500">Products and services to feature on your offerings grid</p>
            </div>
          </div>

          {/* Services Section */}
          {(businessType === 'Service' || businessType === 'Both' || !businessType) && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-slate-800">Services</label>
                  {lk.services && <LockedBadge />}
                </div>
                {!lk.services && (
                  <button
                    type="button"
                    onClick={addService}
                    className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer"
                  >
                    <i className="ri-add-line" /> Add Service
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                {services.length === 0 && (
                  <div className="p-4 border border-dashed border-slate-200 rounded-xl text-center text-slate-400 text-xs">
                    No services currently listed. Click "Add Service" to include your primary offerings.
                  </div>
                )}
                {services.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50/70 p-2.5 rounded-xl border border-slate-200">
                    <input
                      value={s.name}
                      onChange={(e) => !lk.services && updateService(i, 'name', e.target.value)}
                      readOnly={!!lk.services}
                      placeholder="Service Name (e.g. SEO Consultation)"
                      className={`col-span-5 px-3 py-2 rounded-lg border text-sm outline-none ${
                        lk.services
                          ? 'bg-white border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                    <input
                      value={s.description}
                      onChange={(e) => !lk.services && updateService(i, 'description', e.target.value)}
                      readOnly={!!lk.services}
                      placeholder="Short description (optional)"
                      className={`col-span-6 px-3 py-2 rounded-lg border text-sm outline-none ${
                        lk.services
                          ? 'bg-white border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                    <div className="col-span-1 flex justify-center">
                      {!lk.services && (
                        <button
                          type="button"
                          onClick={() => removeService(i)}
                          className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products Section */}
          {(businessType === 'Product' || businessType === 'Both') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-slate-800">Products</label>
                  {lk.products && <LockedBadge />}
                </div>
                {!lk.products && (
                  <button
                    type="button"
                    onClick={addProduct}
                    className="text-xs font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-1 cursor-pointer"
                  >
                    <i className="ri-add-line" /> Add Product
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                {products.length === 0 && (
                  <div className="p-4 border border-dashed border-slate-200 rounded-xl text-center text-slate-400 text-xs">
                    No products currently listed. Click "Add Product" to configure your items.
                  </div>
                )}
                {products.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50/70 p-2.5 rounded-xl border border-slate-200">
                    <input
                      value={p.name}
                      onChange={(e) => !lk.products && updateProduct(i, 'name', e.target.value)}
                      readOnly={!!lk.products}
                      placeholder="Product Name"
                      className={`col-span-5 px-3 py-2 rounded-lg border text-sm outline-none ${
                        lk.products
                          ? 'bg-white border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                    <input
                      value={p.description}
                      onChange={(e) => !lk.products && updateProduct(i, 'description', e.target.value)}
                      readOnly={!!lk.products}
                      placeholder="Short description (optional)"
                      className={`col-span-6 px-3 py-2 rounded-lg border text-sm outline-none ${
                        lk.products
                          ? 'bg-white border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                    <div className="col-span-1 flex justify-center">
                      {!lk.products && (
                        <button
                          type="button"
                          onClick={() => removeProduct(i)}
                          className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Card 4: Contact & Channels ─────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5 border-b border-slate-100 pb-4">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <i className="ri-map-pin-line text-emerald-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Contact & Location Channels</h2>
              <p className="text-xs text-slate-500">Contact information displayed on your Contact page and footer</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Business Address" locked={lk.business_address}>
                <TextInput value={businessAddress} onChange={setBusinessAddress} locked={!!lk.business_address} placeholder="Office address, building name, area" />
              </FormField>
            </div>
            <FormField label="City" locked={lk.city}>
              <TextInput value={city} onChange={setCity} locked={!!lk.city} placeholder="City" />
            </FormField>
            <FormField label="State" locked={lk.state}>
              <TextInput value={state} onChange={setState} locked={!!lk.state} placeholder="State / Province" />
            </FormField>
            <FormField label="Country" locked={lk.country}>
              <TextInput value={country} onChange={setCountry} locked={!!lk.country} placeholder="Country" />
            </FormField>
            <FormField label="Postal Code" locked={lk.postal_code}>
              <TextInput value={postalCode} onChange={setPostalCode} locked={!!lk.postal_code} placeholder="Postal Code" />
            </FormField>
            <FormField label="Phone Number" locked={lk.business_phone}>
              <TextInput value={businessPhone} onChange={setBusinessPhone} locked={!!lk.business_phone} placeholder="+91 9876543210" type="tel" />
            </FormField>
            <FormField label="Business Email" locked={true} helper="Derived from your authenticated platform account.">
              <TextInput value={businessEmail} onChange={setBusinessEmail} locked={true} />
            </FormField>

            <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Social Media Links (Optional)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: 'Facebook', value: facebookUrl, setter: setFacebookUrl, locked: lk.facebook_url, icon: 'ri-facebook-circle-line' },
                  { label: 'Instagram', value: instagramUrl, setter: setInstagramUrl, locked: lk.instagram_url, icon: 'ri-instagram-line' },
                  { label: 'LinkedIn', value: linkedinUrl, setter: setLinkedinUrl, locked: lk.linkedin_url, icon: 'ri-linkedin-box-line' },
                  { label: 'Twitter / X', value: twitterUrl, setter: setTwitterUrl, locked: lk.twitter_url, icon: 'ri-twitter-x-line' },
                  { label: 'YouTube', value: youtubeUrl, setter: setYoutubeUrl, locked: lk.youtube_url, icon: 'ri-youtube-line' },
                ].map(({ label, value, setter, locked, icon }) => (
                  <div key={label} className="flex items-center gap-2">
                    <i className={`${icon} text-slate-400 text-base w-5`} />
                    <input
                      value={value || ''}
                      onChange={(e) => !locked && setter(e.target.value)}
                      readOnly={!!locked}
                      placeholder={locked ? '' : `${label} URL`}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
                        locked
                          ? 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed select-none'
                          : 'bg-white border-slate-300 text-slate-900 focus:border-teal-500 focus:ring-2 focus:ring-teal-100'
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Card 5: Audience & Call-to-Action ───────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5 border-b border-slate-100 pb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
              <i className="ri-compass-3-line text-amber-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Audience & Primary Conversion</h2>
              <p className="text-xs text-slate-500">Fine-tune the copy tone and CTA button behavior across all pages</p>
            </div>
          </div>

          <div className="space-y-5">
            <FormField label="Target Customer / Ideal Audience" locked={lk.customer_description} helper="Who is this website speaking to? This guides the copywriter's tone and value proposition.">
              <TextAreaInput
                value={customerDescription}
                onChange={setCustomerDescription}
                locked={!!lk.customer_description}
                placeholder="e.g. Mid-market B2B enterprises looking to scale their digital presence"
                rows={2}
              />
            </FormField>

            {/* Recommended Field: Preferred CTA */}
            <div className="bg-amber-50/50 border border-amber-200/70 rounded-xl p-4">
              <FormField
                label="Preferred Call-to-Action (CTA)"
                recommended
                helper="Sets the primary action button text across hero banners, service cards, and sticky footer buttons."
              >
                <div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {CTA_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setPreferredCta(preset);
                          setCustomCta('');
                        }}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                          preferredCta === preset
                            ? 'bg-teal-700 text-white border-teal-700 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-teal-400 hover:text-teal-700'
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPreferredCta('__custom__')}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                        preferredCta === '__custom__'
                          ? 'bg-teal-700 text-white border-teal-700 shadow-xs'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-teal-400 hover:text-teal-700'
                      }`}
                    >
                      Custom...
                    </button>
                  </div>

                  {preferredCta === '__custom__' && (
                    <input
                      value={customCta}
                      onChange={(e) => setCustomCta(e.target.value)}
                      placeholder='Type your custom button text, e.g. "Start 14-Day Free Trial"'
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                    />
                  )}
                </div>
              </FormField>
            </div>
          </div>
        </div>

        {/* ── Submit Action Card ─────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-slate-900">Ready to build your website?</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Your configurations will generate your tailored sitemap and custom AI-authored page sections.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-7 py-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold rounded-xl text-sm transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Configuring Profile...</span>
              </>
            ) : (
              <>
                <span>Proceed to Website Builder</span>
                <i className="ri-arrow-right-line" />
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}

